import { Controller } from "@hotwired/stimulus";
import useStore from "@/modules/store";

export default class FormValidationController extends Controller {
  static targets = [
    "input",
    "fileInput",
    "errorHelper",
    "submitButton",
    "submitText",
    "submitSpinner",
    "submitHint",
  ];
  static values = { lock: Boolean, loadingText: String };

  initialize() {
    this.isLocked = false;
  }

  submit(event) {
    if (this.lockValue && this.isLocked) {
      event.preventDefault();
      return;
    }

    let isFormValid = true;
    const { selects, setSelectValue } = useStore.getState();

    const isDisplayNone = (element) => {
      while (element && element !== document) {
        const style = window.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden") {
          return true;
        }
        element = element.parentElement;
      }
      return false;
    };

    Object.entries(selects).forEach(([id, state]) => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }

      const isHidden = isDisplayNone(element);

      if (!isHidden) {
        if (state.required && (!state.value || state.value.length === 0)) {
          setSelectValue(id, state.value, state.required, true);
          isFormValid = false;
        } else {
          setSelectValue(id, state.value, state.required, false);
        }
      } else {
        setSelectValue(id, state.value, false, false);
      }
    });

    this.inputTargets.forEach((input) => {
      const isHidden = isDisplayNone(input);

      if (!isHidden) {
        if (input.required && !input.value) {
          input.removeAttribute("placeholder");
          input.classList.remove("myinitial");
          isFormValid = false;
        }
      }
    });

    this.fileInputTargets.forEach((input) => {
      const isHidden = isDisplayNone(input);

      if (!isHidden) {
        const fieldName = input.dataset.directUploadFieldName;
        let hasCachedUpload = false;
        if (fieldName) {
          const cachedField = this.element.querySelector(
            `input[type="hidden"][data-direct-upload-field-name="${fieldName}"]`,
          );
          if (cachedField && cachedField.value) {
            hasCachedUpload = true;
          }
        }

        if (input.required && !(input.files.length > 0) && !hasCachedUpload) {
          input.classList.remove("myinitial");
          isFormValid = false;
        }
      }
    });

    this.errorHelperTargets.forEach((errorHelper) => {
      const associatedFieldId = errorHelper.dataset.associatedField;
      const associatedField = document.getElementById(associatedFieldId);

      if (associatedField && !isDisplayNone(associatedField.parentElement)) {
        if (!associatedField.value) {
          errorHelper.classList.add("block");
          isFormValid = false;
        } else {
          errorHelper.classList.remove("block");
        }
      } else {
        errorHelper.classList.remove("block");
      }
    });

    if (!isFormValid) {
      event.preventDefault();
      return;
    }

    if (this.lockValue) {
      this.lockSubmit(event);
    }
  }

  lockSubmit(event) {
    this.isLocked = true;
    const button = this.resolveSubmitButton(event);
    const loadingText = this.hasLoadingTextValue
      ? this.loadingTextValue
      : "正在提交，请稍候...";

    if (button) {
      setTimeout(() => {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.setAttribute("aria-busy", "true");
      }, 0);

      if (this.hasSubmitTextTarget) {
        this.submitTextTarget.textContent = loadingText;
      } else if (button.tagName === "INPUT") {
        button.value = loadingText;
      }
    }

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.remove("hidden");
    }

    if (this.hasSubmitHintTarget) {
      this.submitHintTarget.classList.remove("hidden");
    }
  }

  resolveSubmitButton(event) {
    if (this.hasSubmitButtonTarget) {
      return this.submitButtonTarget;
    }

    const currentTarget = event?.currentTarget;
    if (currentTarget instanceof HTMLElement) {
      if (currentTarget.matches("button, input[type='submit']")) {
        return currentTarget;
      }
      const closestButton = currentTarget.closest("button, input[type='submit']");
      if (closestButton) {
        return closestButton;
      }
    }

    return this.element.querySelector("button[type='submit'], input[type='submit']");
  }
}
